import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Store, ChefHat, Loader2, ShieldCheck } from "lucide-react";

const maskCPF = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskCNPJ = (v) => v.replace(/\D/g, "").slice(0, 14)
  .replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
  .replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");

export default function CompleteProfile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState(user?.role && user.role !== "pending" ? user.role : "");
  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    specialty: user?.specialty || "Açougueiro",
    city: user?.city || "",
    phone: user?.phone || "",
    document: "",
  });
  const [loading, setLoading] = useState(false);
  const setField = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const setDocument = (v) => setField("document", role === "comercio" ? maskCNPJ(v) : maskCPF(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!role) return toast.error("Escolha um perfil");
    setLoading(true);
    try {
      await api.post("/auth/complete-profile", {
        role,
        business_name: form.business_name || null,
        specialty: role === "acougueiro" ? form.specialty : null,
        city: form.city || null,
        phone: form.phone || null,
        document: form.document.replace(/\D/g, "") || null,
      });
      await refresh();
      toast.success("Perfil concluído!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-10 relative z-10">
      <div className="w-full max-w-lg">
        <div className="cw-card p-8 anim-fade-up">
          <h1 className="text-2xl font-display font-bold mb-1">Falta pouco, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-sm text-zinc-400 mb-6">Complete seu perfil para começar a usar o CarnesWork.</p>

          {!role ? (
            <div className="grid gap-3">
              <button onClick={() => setRole("comercio")} data-testid="complete-role-comercio"
                      className="text-left p-5 rounded-xl border border-zinc-800 hover:border-red-600 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-lg bg-red-500/15 flex items-center justify-center">
                    <Store className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="font-semibold text-lg">Sou Comércio</div>
                </div>
                <div className="text-sm text-zinc-400">Publique diárias e contrate profissionais.</div>
              </button>
              <button onClick={() => setRole("acougueiro")} data-testid="complete-role-acougueiro"
                      className="text-left p-5 rounded-xl border border-zinc-800 hover:border-red-600 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-lg bg-amber-500/15 flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="font-semibold text-lg">Sou Profissional</div>
                </div>
                <div className="text-sm text-zinc-400">Encontre diárias e ganhe reputação.</div>
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <button type="button" onClick={() => setRole("")} className="text-xs text-zinc-500 mb-2 hover:text-white">← Alterar perfil</button>
              {role === "comercio" && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Nome do estabelecimento</label>
                  <input required className="cw-input mt-1" value={form.business_name} data-testid="complete-input-business"
                         onChange={e => setField("business_name", e.target.value)} placeholder="Açougue do Zé" />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">{role === "comercio" ? "CNPJ" : "CPF"}</label>
                <input className="cw-input mt-1" value={form.document} data-testid="complete-input-document"
                       onChange={e => setDocument(e.target.value)}
                       placeholder={role === "comercio" ? "00.000.000/0000-00" : "000.000.000-00"} />
                <div className="text-[10px] text-zinc-500 mt-1">
                  <ShieldCheck className="w-3 h-3 inline mr-1" />
                  Você pode preencher depois pelo Perfil.
                </div>
              </div>
              {role === "acougueiro" && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Especialidade</label>
                  <select className="cw-input mt-1" value={form.specialty} data-testid="complete-select-specialty"
                          onChange={e => setField("specialty", e.target.value)}>
                    <option>Açougueiro</option>
                    <option>Balconista</option>
                    <option>Desossador</option>
                    <option>Auxiliar</option>
                  </select>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Cidade</label>
                  <input className="cw-input mt-1" value={form.city} data-testid="complete-input-city"
                         onChange={e => setField("city", e.target.value)} placeholder="São Paulo" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Telefone</label>
                  <input className="cw-input mt-1" value={form.phone} data-testid="complete-input-phone"
                         onChange={e => setField("phone", e.target.value)} placeholder="(11) 99999-9999" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="cw-btn-primary w-full flex items-center justify-center gap-2 mt-2" data-testid="btn-complete-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando…</> : "Concluir cadastro"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
